import { Injectable } from "@angular/core";
import { Observable, tap } from "rxjs";
import { BaseService } from "./base.service";

@Injectable({
    providedIn: "root",
})
export class BaseApiService<T> extends BaseService<T> {
    /**
     * Fetches a list of items from the server.
     *
     * @returns An observable that emits an array of items of type T.
     */
    public list(): Observable<T[]> {
        return this._httpClient.get<T[]>(this.URL).pipe(tap((res: any) => this._items.next(res)));
    }

    /**
     * Fetches a single item by its ID from the server.
     *
     * @param id - The ID of the item to fetch.
     * @returns An observable that emits the fetched item of type T.
     */
    public show(id: string): Observable<T> {
        return this._httpClient.get<T>(`${this.URL}/${id}`).pipe(tap((res: T) => this._item.next(res)));
    }

    /**
     * Creates a new item on the server.
     *
     * @param params - The parameters for the new item.
     * @returns An observable that emits the created item of type T.
     */
    public create(params: any): Observable<T> {
        params = this._formatParams(params);
        return this._httpClient.post<T>(this.URL, params).pipe(
            tap((res: T): void => {
                const items: T[] = this._items.value;
                items.push(res);
                this._items.next(items);
            }),
        );
    }

    /**
     * Updates an existing item on the server.
     *
     * @param params - The parameters for the item to update, including its ID.
     * @param id
     * @returns An observable that emits the updated item of type T.
     */
    public update(params: any, id?: string | undefined): Observable<T> {
        id = id ?? params.id;
        params._method = "PUT";
        params = this._formatParams(params);
        return this._httpClient.post<T[]>(`${this.URL}/${id}`, params).pipe(
            tap((res: any) => {
                const items: T[] = this._items.value.map((item: T | any) => (item.id === id ? res : item));
                this._items.next(items);
            }),
        );
    }

    /**
     * Updates an existing item on the server.
     *
     * @param params - The parameters for the item to update, including its ID.
     * @param id
     * @returns An observable that emits the updated item of type T.
     */
    public updatePost(params: any, id?: string | undefined): Observable<T> {
        id = id ?? params.id;
        params = this._formatParams(params);
        return this._httpClient.put<T[]>(`${this.URL}/${id}`, params).pipe(
            tap((res: any) => {
                const items: T[] = this._items.value.map((item: T | any) => (item.id === id ? res : item));
                this._items.next(items);
            }),
        );
    }

    /**
     * Deletes an item by its ID from the server.
     *
     * @param id - The ID of the item to delete.
     * @returns An observable that emits the result of the deletion.
     */
    public delete(id: any): Observable<any> {
        return this._httpClient.delete(`${this.URL}/${id}`).pipe(
            tap((): void => {
                this._items.next(this._items.value.filter((item: T | any): boolean => item.id !== id));
            }),
        );
    }

    public resetItem() {
        this._item.next(null);
    }
}
